import React, {CSSProperties, ReactElement, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AssetMetaInterface, ProjectAssetLink} from '@taopix/taopix-core/dist/types/types';
import {
  Button,
  Checkbox,
  ChevronLeftIcon,
  FolderIcon,
  Icon,
  Theme,
  ThemeName,
} from '@taopix/taopix-design-system';
import MediaGallery, {HideMode} from '../../MediaGallery/MediaGallery';
import {PictureSortType} from '../AddPicturesButton';
import {PreviousUploadComponentProps} from '../PreviousUploads/PreviousUploadsFactory';
import {AssetLoadResponseAdapter, AssetStatus} from '@taopix/taopix-core';
import useSettings from '../../../Hooks/useSettings';
import MediaGalleryAlbumThumbnail from '../../MediaGallery/MediaGalleryAlbumThumbnail';
import classNames from 'classnames';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {useAssetServices} from '../../Context/AssetServices/AssetServicesContext';
import {AssetServicesActions} from '../../Context/AssetServices/AssetServicesActions';
import LoadOrFailPanel from './LoadOrFailPanel';

type ProjectList = {
  projectList: Record<number, ProjectEntry>;
};

type ProjectEntry = {
  name: string;
  assets: Record<number, Partial<ProjectAssetLink & {assetId: number}>>;
  ref?: string;
};

type AssetDetails = {
  assets: Partial<AssetMetaInterface>[];
};

type ProjectFolder = {
  id: string;
  src: string | ReactElement;
  name: string;
  pictureCount: number;
};

type ProjectViewProps = PreviousUploadComponentProps & {
  onPopulated: () => void;
};

const ProjectView = ({
  usage,
  server,
  projectImages,
  hideUsed,
  thumbnailLayout,
  onPopulated,
}: ProjectViewProps) => {
  const settings = useSettings();
  const ui = useUiState();
  const services = useAssetServices();
  const {t} = useTranslation(['*', 'assetservices', 'picturemanager', 'helptips']);

  const [selectedProject, setSelectedProject] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [showNextPage, setShowNextPage] = useState(false);
  const [limit] = useState<number>(50);
  const [projects, setProjects] = useState<Record<number, ProjectEntry[]>>({});
  const [assets, setAssets] = useState<Partial<AssetMetaInterface>[]>([]);
  const [loading, setIsLoading] = useState(true);
  const [selectAll, setSelectAll] = useState(false);
  const [fetching, setIsFetching] = useState(false); // used to disabled the load more button, without showing the loader

  const onRequestAssetDetails = (assetIds: number[], entry: ProjectEntry, append = false) => {
    server
      .post<AssetDetails>(
        '/api/assets/details',
        {
          assetIds,
        },
        {token: 'token', json: true}
      )
      .then(({data}) => {
        onSetPhotos(data.assets, entry, append);
      })
      .catch(error => setError(error))
      .finally(() => {
        setIsLoading(false);
        setIsFetching(false);
      });
  };

  const onFetchAssetDetails = () => {
    if ('' === selectedProject) {
      return void 0;
    }

    const entry = cache.find(p => selectedProject === p.ref);
    if (!entry) {
      return void 0;
    }

    const assetIds = assets
      .filter(a => AssetStatus.Error === a.status && !a.children?.length)
      .map(a => a.id) as number[];

    onRequestAssetDetails(assetIds, entry, true);
  };

  const onLoadMore = () => {
    setIsFetching(true);
    onFetchAssetDetails();
  };

  useEffect(() => {
    if (Object.prototype.hasOwnProperty.call(projects, page)) {
      return;
    }

    server
      .get<ProjectList>(`/api/assets/projectList/${page}/${limit}`, {token: 'token', json: true})
      .then(({data}) => {
        const totalLength = Object.entries(data.projectList).length;
        const raw = Object.entries(data.projectList)
          .reduce((acc, curr) => {
            const [ref, project] = curr;
            project.ref = ref;
            acc.push(project);
            return acc;
          }, [] as ProjectEntry[])
          .filter(prj => prj.ref !== settings.state.project.ref) // don't show the current project
          .filter(prj => Object.keys(prj.assets).length > 0); // only show projects with assets

        const length = raw.length;
        if (totalLength === limit) {
          setShowNextPage(true);
        } else {
          setShowNextPage(false);
        }
        if (!length) {
          if (page > 0) {
            setPage(page - 1);
          }
          setShowNextPage(false);
          setProjects({...projects, [page]: []});
          setIsLoading(false);
          return;
        }
        setProjects({
          ...projects,
          [page]: raw,
        });
        setIsLoading(false);
        onPopulated();
      });
  }, [page, limit, server]);

  useEffect(() => {
    if ('' === selectedProject) {
      return void 0;
    }

    const entry = cache.find(p => selectedProject === p.ref);
    if (!entry) {
      return void 0;
    }

    // if we don't have any assets, don't call the server
    const assetIds = Object.values(entry.assets).map(asset => asset.assetId);
    if (!assetIds.length) {
      return void 0;
    }

    setSelectAll(false);
    setIsLoading(true);

    onRequestAssetDetails(assetIds as number[], entry);
  }, [selectedProject]);

  useEffect(() => {
    if (selectAll) {
      onSelectAll(selectAll);
    }
  }, [assets, selectAll]);

  const onShowNextPage = () => {
    setPage(page + 1);
  };

  /**
   * flatten and memoise the project pages
   */
  const cache = useMemo(() => {
    return Object.values(Object.entries(projects))
      .map(entry => entry[1])
      .flat();
  }, [projects]);

  // create the folder list
  const folders = useMemo(() => {
    return cache.map(entry => {
      return {
        id: entry.ref as string,
        src: <Icon icon={<FolderIcon />} size={'large'} />,
        name: entry.name,
        pictureCount: Array.isArray(entry.assets) ? 0 : Object.values(entry.assets).length,
      };
    });
  }, [cache]);

  /**
   * build a list of ids from the current project assets
   */
  const disabledAssetIds = useMemo(() => {
    return (
      (projectImages
        .map(asset => {
          return asset.id;
        })
        .filter(a => a) as number[]) || []
    );
  }, [projectImages]);

  /**
   * build a list of assets, excluding disabledAssetIds
   */
  const availableAssets = useMemo(() => {
    if (!assets.length) {
      return [];
    }
    return assets.filter(asset => !disabledAssetIds.includes(asset.id as number));
  }, [assets, disabledAssetIds]);

  /**
   * build a list of fully loaded assets
   */
  const validAssets = useMemo(() => {
    return assets.filter(a => AssetStatus.Processed === a.status);
  }, [assets]);

  /**
   * Covert the ProjectLink records into AssetMetaInterface and set in state
   *
   * @param payload
   * @param project
   */
  const onSetPhotos = useCallback(
    (payload: Partial<AssetMetaInterface>[], project: ProjectEntry, append = false) => {
      const converted = AssetLoadResponseAdapter(
        Object.values(payload).map(asset => {
          return {
            ...project.assets[asset.id as number],
            asset: asset,
          };
        }) as unknown as ProjectAssetLink[],
        settings.state
      );

      setAssets(current => {
        if (!append) {
          return converted;
        }
        return [...validAssets, ...converted];
      });
    },
    [validAssets, selectAll]
  );

  const onSelectProject = (folder: ProjectFolder) => {
    // set the selected album id
    setSelectedProject(String(folder.id));
  };

  /**
   *
   * @param checked
   * @param pic
   */
  const onSelectAsset = (checked: boolean, pic: AssetMetaInterface) => {
    // create a map of the current selected assets
    const selected = new Map<number, AssetMetaInterface>();
    services.state.assets.forEach(a => {
      selected.set(a.id, a);
    });

    if (AssetStatus.Error === pic.status) {
      return void 0;
    }
    if (checked) {
      if (!selected.has(pic.id)) {
        selected.set(pic.id, pic);
      }
    } else {
      if (selected.has(pic.id)) {
        selected.delete(pic.id);
      }
    }
    services.dispatch(AssetServicesActions.setSelectedAssets(Array.from(selected.values())));
  };

  const onSelectAll = useCallback(
    (checked: boolean) => {
      setSelectAll(checked);

      // create a map of the current selected assets
      const selected = new Map<number, AssetMetaInterface>();
      services.state.assets.forEach(a => {
        selected.set(a.id, a);
      });

      assets
        .filter(image => {
          return !disabledAssetIds?.includes(image.id as number);
        })
        .filter(image => AssetStatus.Error !== image.status)
        .forEach(asset => {
          if (checked) {
            if (!selected.has(asset.id as number)) {
              selected.set(asset.id as number, asset as AssetMetaInterface);
            }
          } else {
            if (selected.has(asset.id as number)) {
              selected.delete(asset.id as number);
            }
          }
        });
      services.dispatch(AssetServicesActions.setSelectedAssets(Array.from(selected.values())));
    },
    [assets, disabledAssetIds, hideUsed]
  );

  // TODO: move this into it's own component
  const Error = ({error}: {error: string}) => {
    if (error) {
      return (
        <Theme name={ThemeName.Warning} className="px-sm py-xs mt-sm">
          {error}
        </Theme>
      );
    } else {
      return <></>;
    }
  };

  const galleryClassNames = classNames(
    'flex',
    'flex-wrap',
    'space-x-[var(--grid-gap)]',
    '-mt-[var(--grid-gap)]',
    '-ml-[var(--grid-gap)]'
  );

  // if we are not authenticated, show the auth view
  return (
    <>
      <div
        id="nav"
        className="flex sticky top-0 z-20 bg-themeBackgroundColour justify-between pb-sm"
      >
        {selectedProject && (
          <Button
            disabled={'' === selectedProject}
            buttonStyle="tertiary"
            size={'small'}
            startIcon={<ChevronLeftIcon />}
            label={t('str_LabelBack')}
            onClick={() => {
              setSelectedProject('');
              setAssets([]);
            }}
          />
        )}
        {!['', '-1'].includes(String(selectedProject)) ? (
          <Checkbox
            label={t('*:str_LabelSelectAllPictures')}
            id={'project-view-select-group'}
            onChange={isChecked => onSelectAll(isChecked)}
            defaultChecked={false}
            disabled={false}
            className="mr-24"
          />
        ) : (
          <></>
        )}
      </div>
      <Error error={error} />
      {['', '-1'].includes(String(selectedProject)) ? (
        <div
          className={'overflow-auto flex flex-col flex-1'}
          style={{'--grid-gap': '16px'} as CSSProperties}
        >
          <>
            {loading || folders.length === 0 ? (
              <LoadOrFailPanel
                status={loading ? 'loading' : 'failed'}
                failureMessage={t('str_MessageNoPreviousProjects', {
                  ns: 'assetservices',
                })}
              />
            ) : (
              <>
                <ul className={galleryClassNames}>
                  {folders.map((folder: ProjectFolder, index: number) => {
                    return (
                      <MediaGalleryAlbumThumbnail
                        albumId={folder.id}
                        imgSource={folder.src}
                        albumName={folder.name}
                        onSelect={() => onSelectProject(folder)}
                        pictureCount={folder.pictureCount}
                        key={`${folder.id}-${index}`}
                        columns={ui.state.isSmallScreen ? 3 : 5}
                      />
                    );
                  })}
                </ul>
                {showNextPage && (
                  <div className={'flex flex-1 justify-center items-center mt-10'}>
                    <Button
                      buttonStyle={projects[page] ? 'distinct' : 'tertiary'}
                      className={'self-center'}
                      label={
                        projects[page]
                          ? t('helptips:str_ButtonLoadMore')
                          : t('*:str_LabelLoadingEllipsis')
                      }
                      loading={!projects[page]}
                      disabled={!projects[page]}
                      onClick={onShowNextPage}
                    />
                  </div>
                )}
              </>
            )}
          </>
        </div>
      ) : (
        <div className={'overflow-auto flex flex-col flex-1'}>
          {loading || assets.length === 0 ? (
            <LoadOrFailPanel
              status={loading ? 'loading' : 'failed'}
              failureMessage={t('str_MessageProjectHasNoPictures', {
                ns: 'assetservices',
              })}
            />
          ) : (
            <>
              {hideUsed && 0 === availableAssets.length && (
                <Theme name={ThemeName.Warning} className="flex-1 flex justify-center p-md">
                  {t('autofill:str_ErrorAutoFillAllPicturesUsed')}
                </Theme>
              )}
              <MediaGallery
                selectedImages={services.state.assets.map(a => a.id)}
                disabledAssetIds={disabledAssetIds}
                groupedView={true}
                selectableImages={true}
                onSelectPicture={onSelectAsset}
                hideUsed={hideUsed}
                thumbnailLayout={thumbnailLayout}
                columns={undefined}
                sortBy={PictureSortType.Filename}
                sortDirection={'asc'}
                imageList={validAssets as any}
                pictureUsage={usage}
                isExternalService
                currencyCode={settings.state.currency.code}
                hideMode={HideMode.Project}
              />
              {assets.length !== validAssets.length && (
                <div className={'flex flex-1 justify-center items-center mt-10'}>
                  <Button
                    buttonStyle={'distinct'}
                    className={'self-center'}
                    label={t('helptips:str_ButtonLoadMore')}
                    loading={fetching}
                    disabled={fetching}
                    onClick={onLoadMore}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ProjectView;
